import React from "react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Users } from "lucide-react";

/**
 * TournamentFilterBar
 *   Gemensam filter-rad för turneringar: välj målvakt + välj match.
 *   Används både i EvaluateView (ägarens vy) och ShareView (delad vy).
 *
 * Props:
 *   goalkeepers:   [{id, name, sessions_played, total, saves, save_pct}]
 *   matches:       [{id, name, date, opponent, goalkeeper_id, final_score, stats_summary}]
 *   value:         { goalkeeperId: string|null, sessionId: string|null }
 *   onChange:      (next) => void
 */
export const matchGoalkeepers = (m) =>
  m.goalkeepers?.length ? m.goalkeepers : [{ id: m.goalkeeper_id, name: m.goalkeeper_name || "" }];

export const matchPlayedBy = (m, gkId) => matchGoalkeepers(m).some((g) => g.id === gkId);

export default function TournamentFilterBar({
  goalkeepers = [],
  matches = [],
  value,
  onChange,
}) {
  const { goalkeeperId, sessionId } = value || { goalkeeperId: null, sessionId: null };

  // Only show matches the selected goalkeeper played in (if one is picked)
  const visibleMatches = goalkeeperId
    ? matches.filter((m) => matchPlayedBy(m, goalkeeperId))
    : matches;
  const activeMatch = sessionId ? matches.find((m) => m.id === sessionId) : null;
  const activeMatchGks = activeMatch ? matchGoalkeepers(activeMatch) : [];

  const setGoalkeeper = (id) => {
    // Drop selected match if the new goalkeeper didn't play in it.
    const keepSession = sessionId && (id == null || (activeMatch && matchPlayedBy(activeMatch, id)));
    onChange({
      goalkeeperId: id,
      sessionId: keepSession ? sessionId : null,
    });
  };

  const setSession = (id) => onChange({ goalkeeperId, sessionId: id });

  if (goalkeepers.length === 0 && matches.length === 0) return null;

  return (
    <section className="card-tactical p-5 space-y-4" data-testid="tournament-filter-bar">
      {goalkeepers.length > 1 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
            Målvakt
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterPill
              testid="tfb-gk-all"
              active={goalkeeperId == null}
              onClick={() => setGoalkeeper(null)}
            >
              <Users size={12} className="mr-1.5" />
              Alla
            </FilterPill>
            {goalkeepers.map((g) => (
              <FilterPill
                key={g.id}
                testid={`tfb-gk-${g.id}`}
                active={goalkeeperId === g.id}
                onClick={() => setGoalkeeper(g.id)}
              >
                {g.name}
                <span className="ml-2 text-[10px] text-zinc-500 font-mono-num">
                  {g.save_pct.toFixed(0)}%
                </span>
              </FilterPill>
            ))}
          </div>
        </div>
      )}

      {visibleMatches.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
            Match
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterPill
              testid="tfb-match-all"
              active={sessionId == null}
              onClick={() => setSession(null)}
            >
              Alla matcher
            </FilterPill>
            {visibleMatches.map((m) => {
              const dateLabel = m.date
                ? format(new Date(m.date), "d MMM", { locale: sv })
                : "";
              return (
                <FilterPill
                  key={m.id}
                  testid={`tfb-match-${m.id}`}
                  active={sessionId === m.id}
                  onClick={() => setSession(m.id)}
                >
                  {m.opponent ? `vs ${m.opponent}` : m.name}
                  {dateLabel && (
                    <span className="ml-2 text-[10px] text-zinc-500">
                      {dateLabel}
                    </span>
                  )}
                </FilterPill>
              );
            })}
          </div>
        </div>
      )}

      {activeMatch && activeMatchGks.length > 1 && (
        <div data-testid="tfb-match-goalkeepers">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
            Målvakter i matchen · välj vem du vill utvärdera
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterPill
              testid="tfb-match-gk-all"
              active={goalkeeperId == null}
              onClick={() => onChange({ goalkeeperId: null, sessionId })}
            >
              <Users size={12} className="mr-1.5" />
              Alla
            </FilterPill>
            {activeMatchGks.map((g) => (
              <FilterPill
                key={g.id}
                testid={`tfb-match-gk-${g.id}`}
                active={goalkeeperId === g.id}
                onClick={() => onChange({ goalkeeperId: g.id, sessionId })}
              >
                {g.name}
                {g.total > 0 && (
                  <span className="ml-2 text-[10px] text-zinc-500 font-mono-num">
                    {g.save_pct.toFixed(0)}% · {g.saves}/{g.total}
                  </span>
                )}
              </FilterPill>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * TournamentMatchList – list of matches; filtered by selected GK, click toggles match.
 */
export function TournamentMatchList({ matches = [], value, onChange, testid = "tournament-matches", itemPrefix = "tournament-match" }) {
  const { goalkeeperId, sessionId } = value;
  const visible = goalkeeperId ? matches.filter((m) => matchPlayedBy(m, goalkeeperId)) : matches;
  if (visible.length === 0) return null;
  return (
    <section className="card-tactical p-5" data-testid={testid}>
      <div className="section-hdr mb-4">
        <span className="marker" />
        Matcher i turneringen
      </div>
      <div className="space-y-2">
        {visible.map((m) => {
          const gks = matchGoalkeepers(m);
          const own = goalkeeperId ? gks.find((g) => g.id === goalkeeperId) : null;
          const sum = own && own.total != null ? own : m.stats_summary;
          const active = sessionId === m.id;
          return (
            <button
              type="button"
              key={m.id}
              data-testid={`${itemPrefix}-${m.id}`}
              data-active={active}
              onClick={() => onChange({ goalkeeperId, sessionId: active ? null : m.id })}
              className={`w-full flex items-center justify-between p-3 border text-left transition-colors ${
                active ? "border-[#34C759] bg-[#0f1a12]" : "border-[#27272A] hover:border-[#3F3F46]"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-display uppercase tracking-widest text-white">
                  {m.name}
                  {m.opponent && (
                    <span className="text-zinc-500 normal-case font-normal tracking-normal ml-2">
                      vs {m.opponent}
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {gks.map((g) => g.name).filter(Boolean).join(" · ")}
                  {m.date && ` · ${format(new Date(m.date), "d MMM yyyy", { locale: sv })}`}
                  {m.final_score && <span className="ml-2 text-[#FFD60A]">· {m.final_score}</span>}
                </div>
              </div>
              <div className="text-right ml-3 font-mono-num text-sm">
                <div className="text-[#34C759] font-bold text-lg">
                  {sum.total > 0 ? `${sum.save_pct.toFixed(0)}%` : "–"}
                </div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                  {sum.saves}/{sum.total}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function FilterPill({ children, active, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      data-active={active}
      onClick={onClick}
      className={`inline-flex items-center h-9 px-3 text-xs font-display uppercase tracking-widest border transition-colors ${
        active
          ? "border-[#34C759] text-white bg-[#0f1a12]"
          : "border-[#27272A] text-zinc-400 hover:border-[#3F3F46] hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * GoalkeeperComparisonCard
 *   Rendering-only kort som visar varje målvakts aggregerade stats i turneringen.
 */
export function GoalkeeperComparisonCard({ goalkeepers }) {
  if (!goalkeepers || goalkeepers.length < 2) return null;
  return (
    <section className="card-tactical p-5" data-testid="gk-comparison">
      <div className="section-hdr mb-4">
        <span className="marker" />
        Målvakter i turneringen
      </div>
      <div className="space-y-2">
        {goalkeepers.map((g) => {
          const pct = g.save_pct || 0;
          return (
            <div
              key={g.id}
              data-testid={`gk-comparison-row-${g.id}`}
              className="grid grid-cols-[1fr_auto] md:grid-cols-[220px_1fr_auto] items-center gap-3 border border-[#27272A] p-3"
            >
              <div className="min-w-0">
                <div className="font-display uppercase tracking-widest text-white truncate">
                  {g.name}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {g.sessions_played} match{g.sessions_played === 1 ? "" : "er"}
                </div>
              </div>
              <div className="hidden md:block h-2 bg-[#171717] border border-[#27272A] relative">
                <div
                  className="h-full"
                  style={{
                    width: `${pct}%`,
                    background:
                      pct >= 66 ? "#34C759" : pct >= 33 ? "#FFD60A" : "#FF3B30",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
              <div className="text-right">
                <div className="text-[#34C759] font-mono-num font-bold text-lg">
                  {pct ? pct.toFixed(0) : 0}%
                </div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono-num">
                  {g.saves}/{g.total}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
